<template>
    <section v-if="$can('view', 'analytics')">
        <div class="mb-6">
            <div class="d-flex align-center gap-1 mb-2">
                <h4 class="text-h4">{{ $t('menu.order_analytics') }}</h4>
                <!-- The counting rules are reference text: hover on desktop, tap on a phone -->
                <VTooltip location="bottom" max-width="420" open-on-click>
                    <template #activator="{ props: tip }">
                        <VBtn v-bind="tip" icon variant="text" size="small"
                            :aria-label="$t('analytics.about_numbers')">
                            <VIcon icon="tabler-info-circle" />
                        </VBtn>
                    </template>
                    <p class="mb-2">{{ $t('analytics.intro') }}</p>
                    <p v-if="payload" class="mb-0">{{ $t('analytics.no_history', { count: f.number(payload.noHistory) }) }}</p>
                </VTooltip>
            </div>
            <p v-if="payload" class="text-body-2 text-medium-emphasis mb-0">
                {{ $t('analytics.data_as_of', { time: f.dateTime(payload.generatedAt) }) }}
            </p>
        </div>

        <VProgressLinear v-if="loading" indeterminate color="primary" class="mb-6" />

        <VAlert v-else-if="loadError" type="error" variant="tonal" class="mb-6">
            <div class="d-flex flex-wrap align-center justify-space-between gap-4">
                <span>{{ $t('analytics.load_error') }}</span>
                <VBtn color="error" variant="tonal" prepend-icon="tabler-refresh" @click="load">
                    {{ $t('analytics.retry') }}
                </VBtn>
            </div>
        </VAlert>

        <!-- Every section takes :dashboard and sends filter changes back as update:filters patches -->
        <template v-if="dashboard">
            <AnalyticsFilters :filters="filters" :today="payload.today" @update:filters="updateFilters" />
            <AnalyticsTiles :dashboard="dashboard" />
            <AnalyticsTotals :dashboard="dashboard" />
            <AnalyticsDesigners :dashboard="dashboard" />
            <AnalyticsCustomers :dashboard="dashboard" :filters="filters" :customers="customers"
                :today="payload.today" @update:filters="updateFilters" />
            <AnalyticsServicesDefects :dashboard="dashboard" />
            <AnalyticsQuality :dashboard="dashboard" />
        </template>
    </section>
    <VAlert v-else type="warning" variant="tonal">
        {{ $t('admin_notifications.no_permission') }}
    </VAlert>
</template>

<script setup>
definePage({
    meta: {
        action: 'view',
        subject: 'analytics',
    },
})

import AnalyticsCustomers from '@/components/polyhen/analytics/AnalyticsCustomers.vue'
import AnalyticsDesigners from '@/components/polyhen/analytics/AnalyticsDesigners.vue'
import AnalyticsFilters from '@/components/polyhen/analytics/AnalyticsFilters.vue'
import AnalyticsQuality from '@/components/polyhen/analytics/AnalyticsQuality.vue'
import AnalyticsServicesDefects from '@/components/polyhen/analytics/AnalyticsServicesDefects.vue'
import AnalyticsTiles from '@/components/polyhen/analytics/AnalyticsTiles.vue'
import AnalyticsTotals from '@/components/polyhen/analytics/AnalyticsTotals.vue'
import { computeDashboard, presetRange } from '@/components/polyhen/analytics/aggregate'
import { formatters } from '@/components/polyhen/analytics/format'
import { useHead } from '@unhead/vue'
import $api from '~/utils/api'

const { t, locale } = useI18n()
const ability = useAbility()

useHead({ title: computed(() => t('menu.order_analytics')) })

// One row per order (~11k) that is never changed: kept out of deep reactivity
const payload = shallowRef(null)
const loading = ref(false)
const loadError = ref(false)

const filters = ref({
    preset: 'y12',
    from: null,
    to: null,
    cmp: 'prev',
    who: 'customer',
    sortBy: 'area',
    topN: 10,
    added: [],
})

// Patches from the sections: { preset, from, to }, { cmp }, { who }, { sortBy }, { topN },
// { added }
const updateFilters = patch => {
    filters.value = { ...filters.value, ...patch }
}

const dashboard = computed(() => {
    if (!payload.value || !filters.value.from) return null

    return computeDashboard(payload.value, { ...filters.value, today: payload.value.today })
})

const f = computed(() => formatters(locale.value))

// The list to add customers from in the customers section, by name
const customers = computed(() =>
    Object.entries(payload.value?.customers ?? {})
        .map(([id, name]) => ({ id, name: String(name ?? '') }))
        .sort((a, b) => a.name.localeCompare(b.name, 'he'))
)

const load = async () => {
    loading.value = true
    loadError.value = false
    try {
        const { data } = await $api.get('/analytics/orders')
        // Anything else answered with 200 (e.g. the SPA page for an unknown /api URL) is an error
        if (!Array.isArray(data?.orders) || !data.today) {
            throw new Error('Unexpected analytics payload')
        }
        if (!filters.value.from) {
            const [from, to] = presetRange(filters.value.preset, data.today)
            updateFilters({ from, to })
        }
        payload.value = data
    } catch (error) {
        console.error(error)
        loadError.value = true
    } finally {
        loading.value = false
    }
}

onMounted(() => {
    if (ability.can('view', 'analytics')) load()
})
</script>

<style lang="scss">
// Shared by the analytics sections (components/polyhen/analytics)
.analytics-chart {
    position: relative;
    block-size: 280px;
}

.analytics-table {
    font-variant-numeric: tabular-nums;

    // Numbers stay on one line; a long header (one with a date range) wraps instead of pushing
    // the last column out of the card
    td {
        white-space: nowrap;
    }

    .analytics-table__total td {
        font-weight: 600;
    }
}

// A number that opens a filtered list: underlined, in the text colour (not the green primary)
.analytics-link {
    color: inherit;
    text-decoration: underline;
}

.analytics-swatch {
    display: inline-block;
    border-radius: 3px;
    block-size: 10px;
    inline-size: 10px;
    margin-inline-end: 8px;
    vertical-align: -1px;
}
</style>
